import { useState, useRef, useEffect, Fragment, type ReactNode } from "react";
import { Outlet, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { nextPageMountKey } from "@shared/nav-reset";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Ellipsis, Menu } from "lucide-react";
import { useAllowedPages, usePermissionsLoadError, useUserRole } from "@/hooks/use-rbac";
import { isPathAllowed, isUniversalPage } from "@shared/rbac";
import { matchesAdminRoute } from "@/lib/admin-route";
import { readRefusedFrom, REFUSED_FROM_KEY, refusedPageNotice, withoutRefusedFrom } from "@shared/page-reachability";
import { Sidebar } from "./Sidebar";
import { UserMenu } from "./UserMenu";
import { PinSessionChrome } from "./PinSessionChrome";
import { InstallBar } from "./InstallBar";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./NotificationBell";
import { useTheme } from "./ThemeProvider";
import { PageHeaderProvider, usePageHeader } from "./PageHeaderContext";
import { FreshnessBadge, OstendoArmBadge } from "./FreshnessBadge";
import { useOstendoLiveArms, useOstendoLiveFailedArms } from "@/lib/ostendo-live-source";
import { CommandPalette } from "@/components/CommandPalette";
import { AskCrosbotButton } from "@/components/AskCrosbotButton";
import { FloatingPillRailProvider } from "@/components/FloatingPillRail";
import { AdminRouteGate } from "./AdminRouteGate";
import { apiFetch } from "@/lib/api";
import { AboutThisPage } from "@/components/AboutThisPage";
import { Tour, TourOffer, useTour, TOUR_ENABLED } from "@/components/Tour";
import { UpdateToast } from "@/components/UpdateToast";
import { NoticeToast } from "@/components/NoticeToast";
import { WhatsNewModal } from "@/components/WhatsNewModal";
import { useAppIcon } from "@/hooks/use-settings";
import { AnalystProvider } from "@/components/analyst/AnalystProvider";
import { AnalystPanel } from "@/components/analyst/AnalystPanel";
import { useSession } from "@/lib/auth-client";

/**
 * Isolated context consumer — only this component re-renders when the page
 * header changes. It never renders <Outlet /> so there's no cascade back
 * into the page that called useSetPageHeader().
 *
 * The global header carries page identity and navigation only. Page controls
 * live in the content toolbar rendered below it. When reporting a UI placement
 * issue, name the route, control, expected content location, permission gate,
 * and desktop/phone evidence so the fix stays attached to the real page.
 */
function PageHeaderSlot() {
  const header = usePageHeader();
  const navigate = useNavigate();
  if (!header) return <div className="flex-1" />;

  const Icon = header.icon;
  return (
    <div className="flex flex-1 items-center gap-2.5 min-w-0 overflow-hidden">
      {header.backTo && (
        <button
          type="button"
          onClick={() => navigate(header.backTo!.to)}
          aria-label={header.backTo.label}
          // #2629: a button has no href, so the UX probe (scripts/ux-tier1-checks.js,
          // A17) reads the way back from here.
          data-back-to={header.backTo.to}
          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md px-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline">{header.backTo.label}</span>
        </button>
      )}
      {Icon && (
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground hidden sm:block" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight">
          {header.title}
        </p>
        {header.subtitle && (
          <p className="truncate text-xs text-muted-foreground leading-tight hidden md:block">
            {header.subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

export function HeaderOverflowMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative sm:hidden" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="More actions"
        // #4334: 44 px tap target; -mx-1.5 keeps the header's 32 px footprint.
        className="-mx-1.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <Ellipsis className="h-5 w-5" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-border bg-popover p-2 shadow-lg z-50"
          onClick={() => setOpen(false)}
        >
          <ThemeToggle />
        </div>
      )}
    </div>
  );
}

// Three-source freshness (GH #300 Part 2). "Production: Live" reads the live
// postgres feed; the Ostendo COOP/CROS snapshots are separate periodic imports
// with their own — different — cutoffs, so each gets its own honest badge. A
// single "Live" badge would imply the Ostendo data is current too, which it isn't.
function DataFreshness() {
  const { data } = useQuery({
    queryKey: ["data-freshness"],
    queryFn: () =>
      apiFetch<{
        latest: string | null;
        ostendo?: { coop: string | null; cros: string | null; coop_unreachable?: boolean; cros_unreachable?: boolean };
      }>("/api/production/freshness"),
    staleTime: 5 * 60 * 1000,
    // #325: poll every 5 min so a long-lived focused tab doesn't hold the
    // load-time-cached badge. Without this the freshness/CROS badge went stale
    // (the stale-red-badge that bit a user during the CROS key restore).
    refetchInterval: 5 * 60 * 1000,
  });
  // #1798: a page served live has a badge to show even before (or without) a
  // freshness payload, so the live arms join the mount condition.
  const liveArms = useOstendoLiveArms();
  // #3010: a page told "live unavailable" has a badge to show too.
  const failedArms = useOstendoLiveFailedArms();
  const anyLive = liveArms.CROS || liveArms.COOP || failedArms.CROS || failedArms.COOP;

  if (!data && !anyLive) return null;

  const latest = data?.latest ? new Date(data.latest) : null;
  const prodLive = latest ? (Date.now() - latest.getTime()) / (1000 * 60 * 60) < 24 : false;
  const prodValue = latest
    ? prodLive
      ? "Live"
      : latest.toLocaleDateString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short" })
    : "—";
  const prodTitle = latest
    ? `Production monitor (live postgres feed) — latest batch ${latest.toLocaleString("en-AU", { timeZone: "Australia/Sydney" })}`
    : "Production monitor freshness unavailable";

  const coop = data?.ostendo?.coop ?? null;
  const cros = data?.ostendo?.cros ?? null;
  const coopUnreachable = !!data?.ostendo?.coop_unreachable;
  const crosUnreachable = !!data?.ostendo?.cros_unreachable;

  if (!latest && !coop && !cros && !coopUnreachable && !crosUnreachable && !anyLive) return null;

  // #1798: each Ostendo arm's badge says "Live" when the current page read
  // that arm live, else the copy's date (or unreachable) as before. Always
  // mounted; the badge decides for itself whether it has anything to show.
  const armWrap = (badge: ReactNode) => (
    <span className="hidden items-center gap-2.5 2xl:inline-flex">
      <span aria-hidden className="text-muted-foreground/30">·</span>
      {badge}
    </span>
  );
  return (
    <div className="hidden shrink-0 items-center gap-2.5 text-xs lg:flex">
      {latest && <FreshnessBadge label="Production" value={prodValue} live={prodLive} title={prodTitle} />}
      <OstendoArmBadge arm="COOP" snapshot={coop} unreachable={coopUnreachable} wrap={armWrap} />
      <OstendoArmBadge arm="CROS" snapshot={cros} unreachable={crosUnreachable} wrap={armWrap} />
    </div>
  );
}

/**
 * Pages exempt from the #106 width cap below. Akira asked on 2026-09-24 for the
 * QC vs TDS page to fill a bigger screen: it is a dense matrix, not running
 * text, so the long-line problem #106 solves does not apply. Every other page
 * keeps the cap. Add a route here only with the same reason.
 */
const FULL_WIDTH_ROUTES = new Set(["/commercial/qc-vs-tds"]);

/**
 * #3687 REFUSALNOTE: the line the landing page shows after the route guard
 * refused a page. The guard's redirect carries the refused path in
 * location.state. Once the line has rendered, that key is taken back out of
 * the history entry (a replace to the same URL, other state kept), so a
 * reload, or Back and Forward onto the entry, reads a plain arrival and shows
 * nothing. The line itself is held for the entry the replace lands on and
 * dropped on the next navigation after it (a link, a tab switch, Back).
 * The label comes from the catalog, never from the state string itself.
 */
function useRefusalNotice(): string | null {
  const location = useLocation();
  const navigate = useNavigate();
  const refusedFrom = readRefusedFrom(location.state);
  // key null = held across our own replace; a key = held for that entry only.
  const [held, setHeld] = useState<{ text: string; key: string | null } | null>(null);
  useEffect(() => {
    if (refusedFrom) {
      setHeld({ text: refusedPageNotice(refusedFrom), key: null });
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: withoutRefusedFrom(location.state) },
      );
      return;
    }
    setHeld((h) => {
      if (!h) return h;
      if (h.key === null) return { ...h, key: location.key };
      return h.key === location.key ? h : null;
    });
    // location.key changes on every navigation, the replace included.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  if (refusedFrom) return refusedPageNotice(refusedFrom);
  if (held && (held.key === null || held.key === location.key)) return held.text;
  return null;
}

function AppShellInner({ appIcon }: { appIcon?: string }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { conceptPreview } = useTheme();
  const tour = useTour();
  const { pathname, state: locationState } = useLocation();
  const [searchParams] = useSearchParams();
  // #1986: a sidebar click carries a reset nonce in location.state; keying the
  // routed page on the last-seen nonce remounts it (fresh tab/filter state) so
  // clicking the current page's nav item lands on its landing view. In-page
  // navigation (tab switches, deep links) carries no nonce and keeps the mount.
  const pageMountKey = useRef(0);
  pageMountKey.current = nextPageMountKey(pageMountKey.current, locationState);
  const captureMode = searchParams.get("present") === "1";
  const pages = useAllowedPages();
  // #446: route-guard — a role landing on a page not in its allowed_pages (e.g.
  // via a deep link / the old tour) is redirected to its first allowed page,
  // rather than rendering a page whose API will 403 (which looked like access).
  // Mirrors the sidebar's own allowed_pages filtering. "/" is owned by HomeRoute.
  //
  // #1568: the BLOCK decision uses FRESH role data only — never the localStorage
  // cache that useAllowedPages falls back to. The cache can belong to a previous
  // user in the same browser, or predate a server-side grant change (any page
  // consolidation renames the very paths a deep link carries), and a Navigate
  // fired off stale data throws an authorised user away before the live query
  // lands. Cache stays render-only (sidebar, no-flash); denial waits for truth.
  // The server still 403s real overreach, so the window costs nothing.
  const { data: freshRole } = useUserRole();
  const permissionsError = usePermissionsLoadError();
  const freshPages = freshRole?.allowed_pages ?? null;
  const blocked =
    pathname !== "/" &&
    !isUniversalPage(pathname, freshRole?.revoked_pages ?? []) &&
    freshPages !== null &&
    !isPathAllowed(pathname, freshPages);
  const firstAllowed = (freshPages ?? pages)?.find((p) => p !== "*" && p !== "/") ?? "/login";
  // #4733: an ADMIN_ROUTE_PAGES route (matched as React Router matches it)
  // renders AdminRouteGate in place of the page: it reads /me again and mounts
  // the page only for an admin outside a station PIN session; anyone else gets
  // the Admin page's own refusal in place, the URL kept. Round 4: the gate is
  // keyed by pathname, so moving between admin routes in the app (AI Feedback
  // to a conversation) mounts it afresh and reads /me again too.
  const adminRoute = matchesAdminRoute(pathname);
  // #3687 REFUSALNOTE: the redirect above used to land silently. It now
  // carries the refused path in location.state, and the landing page shows one
  // line naming it (see useRefusalNotice).
  const refusalNotice = useRefusalNotice();
  // #4974 round 2: the install bar waits for the shell's first effects, by
  // which time a routed unattended page (the live board) has registered.
  const [shellReady, setShellReady] = useState(false);
  useEffect(() => setShellReady(true), []);

  return (
    <FloatingPillRailProvider presentationMode={captureMode}>
      <div className="flex h-dvh overflow-hidden bg-background print:block print:h-auto print:overflow-visible">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* min-w-0 (#1424): without it this flex item's min-width:auto grows to the
            widest table's intrinsic width and the root's overflow-hidden CLIPS the
            right-hand columns with no scrollbar — page-level overflow-x-auto
            containers never engage. Seen live on Masterbatch BOMs at ~1480px. */}
        {/* standalone-top-inset (#2168): on this column, not the shell root — the
            sidebar is a sibling that pads itself (fixed drawer on phones, static
            rail on lg+), so padding the root would stack two insets on an iPad
            home-screen install at the rail breakpoint. */}
        {/* #3214: the rail measures and publishes --pill-rail-band, and the
            end-of-page spacer below keeps the last control clear without
            shrinking the scrolling main region or leaving a permanent strip. */}
        <div className="standalone-top-inset flex min-w-0 flex-1 flex-col overflow-hidden print:overflow-visible print:pb-0">
          {/* #1951: capture-safe presentation mode removes the whole top band so
              oversampled video frames contain content, not account/browser chrome.
              Normal navigation is unchanged when the explicit query flag is absent. */}
          {/* #4447: a PIN session shows who is at the station and Switch person
              on every page; the strip renders nothing for a Microsoft session. */}
          {!captureMode && <PinSessionChrome />}
          {!captureMode && (
            <header className="flex h-14 min-w-0 items-center gap-3 border-b border-border px-4 lg:px-6 print:hidden">
              <button
                onClick={() => setSidebarOpen(true)}
                // #4334: 44 px tap target; -mx-1.5 keeps the header's 32 px footprint.
                className="-mx-1.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </button>

              <div data-tour="page-header" className="min-w-0 flex-1">
                <PageHeaderSlot />
              </div>

              <DataFreshness />
              {/* #831 concept preview: notification panel — opt-in, invisible until
                  a user turns the preview on in Settings → Appearance. */}
              {conceptPreview && <NotificationBell />}
              {/* Theme controls stay in the app bar; page actions live in content below. */}
              <div className="hidden sm:block">
                <ThemeToggle />
              </div>
              <HeaderOverflowMenu />
              <UserMenu />
            </header>
          )}
          {/* #4974 INSTALLPROMPT: the install line, under the app bar so it
              never covers page content or the floating pills. */}
          {!captureMode && <InstallBar iconKey={appIcon} ready={shellReady} />}

          {/* Main content. #106: cap the readable width on high-res monitors so
              content (dashboards, AI summaries) doesn't stretch edge-to-edge into
              over-long lines. Inert at <=1536px; centres on wider screens. Pages in
              FULL_WIDTH_ROUTES are exempt. Display
              Mode (/display) renders outside this shell, so it stays full-bleed. */}
          {/* #5184 CROSBOTSCROLL: `relative` makes <main> the anchor for absolutely
              positioned things inside a page (the sr-only commas in Sales Pipeline's
              deal rows). Without it they anchor to the document, stretch it past the
              window, and a mouse wheel over the empty Ask Crosbot panel scrolls the
              whole app. */}
          <main className="relative flex-1 overflow-y-auto p-4 lg:p-6 print:overflow-visible print:p-0">
            {/* h-full lets a full-height page (e.g. the Data Analyst chat, whose root is
                `h-full flex-col` with an internal `flex-1 overflow-y-auto` message list)
                cap to the viewport and scroll INTERNALLY, instead of the whole history
                growing the page and pushing the chat input off-screen (Akira 2026-07-01).
                Normal pages taller than this still overflow → <main> scrolls as before. */}
            <div className={`mx-auto h-full w-full ${FULL_WIDTH_ROUTES.has(pathname) ? "max-w-none" : "max-w-[1536px]"}`}>
              {blocked ? (
                <Navigate to={firstAllowed} replace state={{ [REFUSED_FROM_KEY]: pathname }} />
              ) : (
                <Fragment key={pageMountKey.current}>
                  {/* #3307 panel: edit controls are hidden when the permissions
                      read fails; say why above the page, where they would be. */}
                  {permissionsError && (
                    <p role="status" className="mb-3 text-xs text-destructive print:hidden">{permissionsError}</p>
                  )}
                  {refusalNotice && (
                    <p
                      role="status"
                      data-testid="refused-page-notice"
                      className="mb-3 rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground print:hidden"
                    >
                      {refusalNotice}
                    </p>
                  )}
                  {adminRoute ? <AdminRouteGate key={pathname} /> : <Outlet />}
                  <AboutThisPage />
                  {/* #3214: the pills float bottom-right over the scroll end.
                      This spacer adds their measured band after the last thing
                      on every width, so the page can scroll clear of them
                      without shrinking the first screen. #5114: below lg the
                      band also covers every bottom toast showing. */}
                  <div className="h-[var(--pill-rail-band,4.5rem)] print:hidden" aria-hidden="true" />
                </Fragment>
              )}
            </div>
          </main>
        </div>

        {/* #2088: the Analyst panel is a flex sibling of the page column, so
            folding it out narrows the page instead of covering it. It collapses
            to a rail on lg+ and becomes a bottom sheet below that. */}
        <AnalystPanel />

        <CommandPalette />
        <AskCrosbotButton />
        {/* #769: stale-bundle self-refresh. Mounted here so the version check runs
            on every in-app page; silently reloads an idle client on a new deploy,
            or shows a non-blocking toast when busy (chat/unsaved input). */}
        <UpdateToast />
        {/* #3072: the one-line notice channel. Writes that fire from module
            scope (the debounced preference syncs) have nowhere else to say a
            save did not land. */}
        <NoticeToast />
        {/* #1285: moshi-style launch announcements — one batched modal when there
            are published changelog entries newer than this user's seen watermark.
            Server-filtered by RBAC page access; any dismissal acks the batch. */}
        <WhatsNewModal />
        {/* #445: "Take the Tour" pulled from the prod surface (Hugo 2026-06-17) — it
            ignored RBAC (deep-linked roles to pages/Data-Analyst they shouldn't reach)
            and the focus-highlight rendered broken. Code kept; rebuild RBAC-aware for
            the commercial rollout. Flip TOUR_ENABLED to re-enable. */}
        {TOUR_ENABLED && tour.offer && <TourOffer onStart={tour.start} onDismiss={tour.dismissOffer} />}
        {TOUR_ENABLED && tour.active && <Tour onClose={tour.finish} initialStep={tour.initialStep} />}
      </div>
    </FloatingPillRailProvider>
  );
}

export function AppShell() {
  const { data: session } = useSession();
  // Repoints <link rel="apple-touch-icon">/manifest at the user's chosen icon before
  // they can reach the share sheet. iOS reads those tags at add-to-home-screen time.
  // #4974: the install bar shows the same icon.
  const appIcon = useAppIcon();
  return (
    <PageHeaderProvider>
      {/* #2088: one Analyst conversation for the app. Mounted above the routed
          page so a live stream survives navigation. */}
      <AnalystProvider userId={session?.user?.id ?? null}>
        <AppShellInner appIcon={appIcon} />
      </AnalystProvider>
    </PageHeaderProvider>
  );
}
